import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { useSelector } from 'react-redux'
import Cookie from 'js-cookie'
import { Button, notification, Tag, Tooltip } from 'antd'
import { isArray } from 'lodash'
import moment from 'moment'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFileExport, faQuestion, faTimes } from '@fortawesome/pro-solid-svg-icons'

import InternshipFilters, { getActiveFiltersCount } from './InternshipFilters'
import ChangedInternshipSectorModal from './ChangedInternshipSectorModal'
import { InstitutionRoles, InternshipStates, InternshipStatesString, OrderBy } from '../../../../utils/constants'
import { selectUser } from '../../../../reducers/UserReducer'
import { InternshipContext } from '../../../../Context/InternshipsManager/InternshipProvider'
import { InternshipsManagerContext } from '../../../../Context/InternshipsManager/InternshipsManagerProvider'
import {
  ExportKeys,
  getCoordinatorFullname,
  getEndDate,
  getHospitalDepartmentName,
  getRevertedStudentFullname,
  getSchoolName,
  getSchoolYearName,
  getSectionNameOrFull,
  getSectorName,
  getStartDate,
  getState,
  getStateTag,
  getStudentEmail
} from '../../../../utils/entities/internship'
import { getDocument } from '../../../../utils/api/export'
import InternshipAcceptationButton from './InternshipAcceptationButton'
import StudentQuickMenu from '../../../../Components/shared/InternshipsManager/StudentQuickMenu'
import InternshipRejection from '../../../../Components/shared/InternshipsManager/InternshipRejection'
import { acceptInternship, exportInternships, resetToPending } from '../../../../utils/api/internship'
import { isObject, subFlatten } from '../../../../utils'
import { GlobalFiltersContext } from '../../../../Providers/GlobalFiltersProvider'
import { ComponentAction } from '../../../../Components/antd/Tables/DataTable/utils/actions'
import { buildFilters, Filter } from '../../../../Components/antd/Tables/DataTable/utils/tableFilter'
import DataTable from '../../../../Components/antd/Tables/DataTable/DataTable'
import { ColorsPalette } from '../../../../config/colors'
import { onError } from '../../../../utils/apiHelper'
import { getFreeFields } from '../../../../utils/api/studentFreeField'
import { selectTranslate } from '../../../../reducers/LocaleReducer/LocalReducer'
import MountedModal from '../../../../HOC/MountedModal'
import ExportInternshipsModal from '../../../../Components/shared/Exports/ExportInternshipsModal'
import IconButton from '../../../../Components/antd/Buttons/IconButton'
import { InstitutionContext } from '../../../../Providers/InstitutionProvider'
import SchoolNameWithBadge from '../../../../Components/shared/Badges/SchoolNameWithBadge'
import { isInstitutionGroupAdmin } from '../../../../utils/roles'
import InstitutionsSelector from '../../../../Components/shared/Selectors/Institution/InstitutionsSelector'

const {
  ACCEPTED,
  CANCELED,
  PENDING,
  REFUSED,
  PENDING_NO_SECTOR,
  PENDING_STUDENT_DATA_MISSING,
  CREATED,
  SCHEDULE_ADDED,
  SCHEDULE_TO_VALIDATE,
  SCHEDULE_VALIDATED
} = InternshipStates
const MINIMAL_STYLE = { minWidth: '26px', minHeight: '26px' }

const REFUSED_ACTION = 'REFUSED'
const UPDATE_SECTOR_ACTION = 'UPDATE_SECTOR'

const InternshipsTable = ({ data, loading, parameters, storageKey, onParametersChange, onDataChange, onFilterChange, onResetFilters }) => {
  const { internshipFilters, setInternshipFilters } = useContext(GlobalFiltersContext)
  const { onUpdate, refreshQuotas, setLocalInternship } = useContext(InternshipContext)
  const { setStudent, setInternship } = useContext(InternshipsManagerContext)
  const ic = useContext(InstitutionContext)

  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [visibleModal, setVisibleModal] = useState(false)
  const [selectedInternship, setSelectedInternship] = useState({ data: null, action: null })
  const [freeFields, setFreeFields] = useState({})
  const [exportKeys, setExportKeys] = useState([])

  const maxHeight = useMemo(() => `calc(100vh - ${Cookie.get('switch_user_id') ? '240px' : '199px'})`, [Cookie.get('switch_user_id')])

  useEffect(() => {
    getFreeFields(user).then(({ data }) => setFreeFields(data))
  }, [user])

  const renderInternshipState = useCallback(internship => {
    const state = getState(internship)
    const tag = getStateTag(state)

    return <Tag color={tag?.color ?? 'red'}>{t(InternshipStatesString[state])}</Tag>
  }, [t])
  const disableActions = useCallback((data, action) => {
    const baseAction = [action]
    let disabled = true

    if (!user.roles.includes(InstitutionRoles.OBSERVER)) {
      if (action === ACCEPTED) {
        disabled = [...baseAction, CANCELED, PENDING_STUDENT_DATA_MISSING, PENDING_NO_SECTOR, CREATED, SCHEDULE_VALIDATED, SCHEDULE_TO_VALIDATE, SCHEDULE_ADDED].includes(data.state)
      } else if (action === REFUSED) {
        disabled = [...baseAction, CANCELED, PENDING_STUDENT_DATA_MISSING].includes(data.state) || user.roles.includes(InstitutionRoles.NURSE)
      } else if (action === PENDING) {
        disabled = [...baseAction, CANCELED, PENDING_STUDENT_DATA_MISSING].includes(data.state)
      }
    }

    return disabled
  }, [user.roles])

  const columns = useMemo(
    () => [
      {
        title: t('common.department'),
        orderBy: OrderBy.HOSPITAL_DEPARTMENT,
        key: getHospitalDepartmentName,
        id: ExportKeys.HOSPITAL_DEPARTMENT
      },
      {
        title: t('common.sector'),
        orderBy: OrderBy.SECTOR,
        key: getSectorName,
        id: ExportKeys.SECTOR_NAME
      },
      {
        title: t('common.school'),
        orderBy: OrderBy.SCHOOL_ABBREVIATION,
        key: (i) => (
          <SchoolNameWithBadge
            school={i.school}
            renderAbbreviation
          />
        ),
        tooltip: getSchoolName,
        id: [ExportKeys.SCHOOL_NAME, ExportKeys.SCHOOL_ABBREVIATION]
      },
      {
        title: t('common.section'),
        orderBy: OrderBy.SECTION,
        key: getSectionNameOrFull,
        id: ExportKeys.SECTION_NAME
      },
      {
        title: t('School year'),
        orderBy: OrderBy.SCHOOL_YEAR_NAME,
        key: getSchoolYearName,
        id: ExportKeys.SCHOOL_YEAR_NAME
      },
      {
        title: t('Student name'),
        orderBy: OrderBy.STUDENT,
        key: getRevertedStudentFullname,
        id: [ExportKeys.STUDENT_LASTNAME, ExportKeys.STUDENT_FIRSTNAME]
      },
      {
        title: t('data_table.column_title.email'),
        orderBy: OrderBy.EMAIL,
        key: getStudentEmail,
        id: ExportKeys.EMAIL
      },
      {
        title: t('common.state'),
        orderBy: OrderBy.STATE,
        key: (i) => renderInternshipState(i),
        id: ExportKeys.STATE
      },
      {
        title: t('common.coordinator'),
        key: getCoordinatorFullname,
        id: ExportKeys.COORDINATOR
      },
      {
        title: t('common.start'),
        orderBy: OrderBy.START_DATE,
        key: getStartDate,
        id: ExportKeys.START_DATE
      },
      {
        title: t('common.end'),
        orderBy: OrderBy.END_DATE,
        key: getEndDate,
        id: ExportKeys.END_DATE
      },
      ...Object.values(freeFields)
        .filter((field) => field.isBadgeDisplayed)
        .map((field) => ({
          title:
            field.translations[user.language] ||
            field.translations[user.language?.slice(0, 2)] ||
            field.fieldType,
          key: (i) => {
            const value = i.student?.freeFields?.[field.fieldType] ?? ''

            return isObject(value)
              ? Object.entries(value)
                .map(([k, v]) => `${k}: ${v}`)
                .join(', ')
              : value
          },
          id: ExportKeys.STUDENT_FREE_FIELDS + '.' + field.fieldType
        }))
    ],
    [renderInternshipState, freeFields, t]
  )

  const actions = useMemo(() => [
    new ComponentAction({
      Component: InternshipAcceptationButton,
      props: (data) => ({
        disabled: disableActions(data, ACCEPTED),
        internship: data,
        style: MINIMAL_STYLE,
        size: 'small',
        onClick: () => {
          acceptInternship(user, { id: data.id }).then(() => {
            onDataChange(parameters)
            notification.success({ message: t('Internship accepted'), placement: 'bottomRight' })
          })
        }
      }),
      containerClassName: 'antd-button-without-padding'
    }),
    new ComponentAction({
      Component: Button,
      props: (data) => ({
        style: { ...MINIMAL_STYLE, marginLeft: '5px' },
        className: 'antd-button-without-padding',
        children: (
          <Tooltip title={t('Refuse')}>
            <FontAwesomeIcon
              height='14px'
              color={disableActions(data, REFUSED) ? ColorsPalette.GREY : ColorsPalette.RED}
              icon={faTimes}
            />
          </Tooltip>
        ),
        size: 'small',
        disabled: disableActions(data, REFUSED),
        onClick: () => setSelectedInternship({ data, action: REFUSED_ACTION })
      }),
      containerClassName: 'antd-button-without-padding'
    }),
    new ComponentAction({
      Component: Button,
      props: (data) => ({
        style: { ...MINIMAL_STYLE, marginLeft: '5px', marginRight: '5px' },
        className: 'antd-button-without-padding',
        children: (
          <Tooltip title={t('Pending')}>
            <FontAwesomeIcon
              height='14px'
              color={disableActions(data, PENDING) || user.roles.includes(InstitutionRoles.NURSE) ? ColorsPalette.GREY : ColorsPalette.DEEP_BLUE}
              icon={faQuestion}
            />
          </Tooltip>
        ),
        size: 'small',
        disabled: disableActions(data, PENDING) || user.roles.includes(InstitutionRoles.NURSE),
        onClick: () => {
          resetToPending(user, { id: data.id }).then(() => {
            onDataChange(parameters)
            notification.success({ message: t('Internship reset to pending'), placement: 'bottomRight' })
          })
        }
      }),
      containerClassName: 'antd-button-without-padding'
    }),
    new ComponentAction({
      Component: StudentQuickMenu,
      props: {
        noMargin: true,
        onSelectedInternshipInfo: setInternship,
        onSelectedStudentInfo: setStudent,
        afterSectorUpdate: internship => {
          setSelectedInternship({ data: internship, action: UPDATE_SECTOR_ACTION })
          setLocalInternship(internship)
        }
      },
      containerClassName: 'antd-button-without-padding',
      dataKey: 'internship'
    })
  ], [
    t, disableActions, onDataChange, setInternship, setLocalInternship, setStudent, setSelectedInternship, onUpdate, user
  ])

  const handleInstitutionChange = (id) => {
    setInternshipFilters({ ...internshipFilters, institutions: id ?? null })
    onFilterChange()
  }

  const filters = useMemo(() => buildFilters([
    new Filter({
      key: 'filters',
      label: t('Filters'),
      component: () => {
        return (
          <InternshipFilters
            internshipFilters={internshipFilters}
            user={user}
            onFilterChange={onFilterChange}
            useOrder={false}
          />
        )
      }
    })
  ]), [internshipFilters, user, onFilterChange, t])

  const handleExport = useCallback(exportParameters => {
    exportParameters.language = user.language.toLowerCase()

    exportParameters.order = parameters.order

    if (exportParameters.checked) {
      exportParameters.filters = {}

      Object.keys(parameters.filters).forEach(key => {
        const value = parameters.filters[key]

        if (!isArray(value) || value.length > 0) {
          exportParameters.filters[key] = value
        }
      })

      delete exportParameters.checked
    }

    exportParameters.columns = subFlatten(exportKeys)

    if (exportParameters.columns.length > 0) {
      exportParameters.columns.unshift('id')
      exportParameters.columns.push('individualInternshipRequest')
    }

    exportInternships(user, exportParameters).then(({ job }) => getDocument(
      job,
      user,
      () => onError(t('spreadsheet_download.error'))
    )).catch(error => {
      console.error(error.message)
      onError(t('internships_export.error'))
    })
  }, [t, parameters, user, exportKeys])

  return (
    <div style={{ height: maxHeight }}>
      <DataTable
        data={data}
        display={{ date: true }}
        exporter={() => (
          <IconButton icon={faFileExport} onClick={() => setVisibleModal(true)} />
        )}
        metadata={parameters.metadata}
        loading={loading}
        columns={columns}
        actions={actions}
        parameters={parameters}
        storageKey={storageKey}
        getActiveFiltersCount={getActiveFiltersCount}
        onParametersChange={onParametersChange}
        onResetFilters={onResetFilters}
        filters={filters}
        filtersMenu={isInstitutionGroupAdmin(user) && (
          <InstitutionsSelector
            empty
            values={Array.isArray(internshipFilters.institutions) ? null : internshipFilters.institutions ?? null}
            onChange={handleInstitutionChange}
            placeholder={t('common.institutions')}
            style={{ width: '210px', marginLeft: '10px' }}
          />
        )}
        onKeysChanged={setExportKeys}
        customLineStyle={{
          height: '7vh',
          alignItems: 'center'
        }}
      />
      <MountedModal isVisible={visibleModal}>
        <ExportInternshipsModal
          endDate={moment(parameters.period.endDate)}
          startDate={moment(parameters.period.startDate)}
          onCancel={() => setVisibleModal(false)}
          onExport={handleExport}
        />
      </MountedModal>
      {selectedInternship.action === UPDATE_SECTOR_ACTION && (
        <ChangedInternshipSectorModal
          internship={selectedInternship.data}
          sectors={isObject(ic) ? ic.sectorsOrderByName : []}
          visible={!!selectedInternship.data}
          onCancel={() => setSelectedInternship({ data: null, action: null })}
          onUpdate={() => {
            setSelectedInternship({ data: null, action: null })
            refreshQuotas()
          }}
        />
      )}
      {selectedInternship.action === REFUSED_ACTION && (
        <InternshipRejection
          internship={selectedInternship.data}
          onClose={ok => {
            if (ok) {
              refreshQuotas()
            }

            setSelectedInternship({ data: null, action: null })
          }}
        />
      )}
    </div>

  )
}

export default InternshipsTable
